import { Button } from '@/components/ui/Button'
import { CompanyAvatar } from '@/features/applications/ApplicationsTable'
import { cn, formatSalary, relativeTime } from '@/lib/utils'
import type { JobMatch, MatchTier } from '@/types/api'

/** A thin accent along the top edge follows the match tier. */
const TIER_ACCENT: Record<MatchTier, string> = {
  strong: 'bg-brand',
  good: 'bg-brand/45',
  fair: 'bg-warning/60',
  weak: 'bg-line-strong',
}

const TIER_LABEL: Record<MatchTier, string> = {
  strong: 'Strong match',
  good: 'Good match',
  fair: 'Fair match',
  weak: 'Weak match',
}

interface Props {
  match: JobMatch
  onApply: (match: JobMatch) => void
  onPass: (match: JobMatch) => void
  onOpen?: (match: JobMatch) => void
  busy?: boolean
}

/** One match on the dashboard board: who, what, how well it fits, Apply or Pass. */
export function JobCard({ match, onApply, onPass, onOpen, busy }: Props) {
  const { job } = match
  const salary = formatSalary(job.minSalary, job.maxSalary, job.salaryCurrency)
  const applyMode = job.easyApply
    ? job.hasQuestionnaire
      ? 'Quick apply · has questions'
      : 'Quick apply'
    : 'Company site'

  return (
    <article className="card relative flex h-full min-w-0 flex-col overflow-hidden p-4 transition-[border-color,box-shadow] hover:border-brand/25 hover:shadow-float sm:p-5">
      <span
        aria-hidden="true"
        className={cn('absolute inset-x-0 top-0 h-[3px]', TIER_ACCENT[match.matchTier])}
      />

      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <CompanyAvatar name={job.companyName} logoUrl={job.company?.logoUrl} />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-semibold text-ink" title={job.companyName}>
              {job.companyName}
            </p>
            <p className="truncate text-[12px] text-ink-muted" title={job.location ?? undefined}>
              {job.location ?? 'Location not stated'}
            </p>
          </div>
        </div>
        <MatchRing score={match.matchScore} size={44} />
      </header>

      <h3 className="mt-3.5 line-clamp-2 break-words text-[17px] font-semibold leading-snug text-ink">
        {onOpen ? (
          <button type="button" onClick={() => onOpen(match)} className="text-left hover:underline">
            {job.title}
          </button>
        ) : (
          job.title
        )}
      </h3>
      <p className="mt-1 text-[12px] text-ink-muted">
        <span className={cn('font-medium', tierText(match.matchScore))}>
          {TIER_LABEL[match.matchTier]}
        </span>{' '}
        · {relativeTime(job.postedAt)}
      </p>

      {job.skills.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {job.skills.slice(0, 4).map((skill) => {
            const matched = match.matchedSkills.includes(skill)
            return (
              <li
                key={skill}
                className={cn(
                  'rounded-[6px] px-1.5 py-0.5 text-[12px]',
                  matched ? 'bg-brand-soft font-medium text-brand' : 'bg-surface-muted text-ink-muted',
                )}
              >
                {skill}
              </li>
            )
          })}
          {job.skills.length > 4 && (
            <li className="num px-1 py-0.5 text-[12px] text-ink-muted">+{job.skills.length - 4}</li>
          )}
        </ul>
      )}

      <dl className="mb-4 mt-3 space-y-0.5 text-[12px] text-ink-muted">
        {salary && (
          <div className="num text-[13px] font-medium text-ink-soft">
            <dt className="sr-only">Salary</dt>
            <dd>{salary}</dd>
          </div>
        )}
        <div>
          <dt className="sr-only">How to apply</dt>
          <dd className="flex min-w-0 items-center gap-1.5">
            {job.board?.name && (
              <span
                className="max-w-[60%] shrink-0 truncate rounded-pill bg-surface-muted px-2 py-0.5 text-[11px] font-medium text-ink-soft"
                title={`From ${job.board.name}`}
              >
                {job.board.name}
              </span>
            )}
            <span className="truncate">{applyMode}</span>
          </dd>
          {job.recommendedFor.length > 0 && (
            <dd className="truncate" title={job.recommendedFor.join(', ')}>
              Naukri · {job.recommendedFor.join(', ')}
            </dd>
          )}
        </div>
      </dl>

      <footer className="mt-auto flex items-center justify-end gap-1.5 border-t border-line pt-3">
        {match.hasApplied ? (
          <span className="rounded-[8px] bg-brand-soft px-3 py-1.5 text-[13px] font-medium text-brand">
            Applied
          </span>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => onPass(match)} disabled={busy}>
              Pass
            </Button>
            <Button size="sm" className="min-w-[84px]" onClick={() => onApply(match)} disabled={busy}>
              Apply
            </Button>
          </>
        )}
      </footer>
    </article>
  )
}

/** Colour follows the score so strength reads before the number does. */
function ringTone(score: number): string {
  if (score >= 80) return 'text-brand'
  if (score >= 65) return 'text-brand/60'
  if (score >= 50) return 'text-warning'
  return 'text-ink-faint'
}

function tierText(score: number): string {
  if (score >= 65) return 'text-brand'
  if (score >= 50) return 'text-warning'
  return 'text-ink-soft'
}

/** The circular percentage badge on each card and row. */
export function MatchRing({ score, size = 46 }: { score: number; size?: number }) {
  const stroke = 3
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - Math.min(Math.max(score, 0), 100) / 100)
  const compact = size < 44

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${score} percent match`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-ink/[0.08]"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cn('transition-[stroke-dashoffset] duration-700', ringTone(score))}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={cn('num font-semibold text-ink', compact ? 'text-[11px]' : 'text-[13px]')}>
          {score}%
        </span>
        {!compact && (
          <span className="mt-[1px] text-[7px] uppercase tracking-[0.1em] text-ink-muted">Match</span>
        )}
      </div>
    </div>
  )
}
